import _cloneDeep from 'lodash/cloneDeep';
import { updateObject } from 'shared/utility';
import * as actionTypes from 'store/actions/actionTypes';

const initialState = {
  signedInAs: {
    UserId: null,
    FirstName: '',
  },
  postingAs: {
    UserId: null,
    FirstName: '',
  },
  editingProfile: {
    UserId: null,
    FirstName: '',
  },
  emailUpdated: false,
  passwordUpdated: false,
  postingAsUserUpdated: false,
  imageUploaded: false,
  error: null,
  loading: false,
  managedUsers: [],
  members: [],
  userSearchResults: null,
  showProfileForm: false,
};

const fetchProfileStart = (state) => {
  return updateObject(state, {
    loading: true,
  });
};

const fetchProfileFailed = (state, action) => {
  return updateObject(state, {
    error: action.error,
    loading: false,
  });
};

const fetchProfileReturnsNewUser = (state, action) => {
  let signedInAs = { ...state.signedInAs };
  const postingAs = { ...state.postingAs, UserId: action.UserId };
  if (state.signedInAs.UserId === action.UserId || state.signedInAs.UserId === null) {
    signedInAs = { ...state.signedInAs, UserId: action.UserId };
  }
  return updateObject(state, {
    loading: false,
    error: null,
    signedInAs,
    postingAs,
    showProfileForm: true,
  });
};

const fetchProfileSuccess = (state, action) => {
  const userObject = action.User;
  userObject.Logs = action.Logs;
  userObject.ManagedUsers = action.ManagedUsers;
  userObject.Groups = action.Groups;
  userObject.GiftsDraftsCount = action.giftsDraftsCount;
  userObject.KnownUsers = action.KnownUsers;
  const postingAsSameAsSignedInAs =
    typeof action.postingAsSameAsSignedInAs === 'boolean'
      ? action.postingAsSameAsSignedInAs
      : typeof action.isManagedUser === 'boolean'
      ? !action.isManagedUser
      : false;

  const stateUpdated = _cloneDeep(state);
  if (action.emojiSkinTone) stateUpdated.emojiSkinTone = action.emojiSkinTone;

  if (action.userStatus === 'signedInAs') {
    // if the postingAs user and signedInAs user are the same person (they are not postingAs a managed user),
    // then we only call fetchUserProfile set both postingAs and signedInAs as the same returned object to avoid making duplicate server call
    if (postingAsSameAsSignedInAs) {
      return updateObject(stateUpdated, {
        signedInAs: userObject,
        postingAs: userObject,
        error: null,
        loading: false,
        imageUploaded: false,
        managedUsers: action.ManagedUsers,
      });
    }
    return updateObject(stateUpdated, {
      signedInAs: userObject,
      error: null,
      loading: false,
      imageUploaded: false,
      managedUsers: action.ManagedUsers,
    });
  }
  if (action.userStatus === 'postingAs') {
    return updateObject(stateUpdated, {
      postingAs: userObject,
      error: null,
      loading: false,
      imageUploaded: false,
    });
  }
  if (action.userStatus === 'managedUser') {
    return updateObject(stateUpdated, {
      error: null,
      loading: false,
      imageUploaded: false,
    });
  }
  return null;
};

const editEmojiTone = (state, action) => {
  return updateObject(state, {
    emojiSkinTone: action.emojiSkinTone,
  });
};

const uploadProfileImageStart = (state) => {
  return updateObject(state, {
    error: null,
    loading: true,
  });
};

const uploadProfileImageFailed = (state, action) => {
  return updateObject(state, {
    error: action.error,
    loading: false,
  });
};

const setProfileImage = (state, action) => {
  return updateObject(state, {
    editingProfile: {
      ...state.editingProfile,
      profileImageId: action.profileImageId,
      ImageURL: action.ImageURL,
      ThumbURL: action.ThumbURL,
    },
    error: null,
    loading: false,
    imageUploaded: true,
  });
};

const submitProfileStart = (state) => {
  return updateObject(state, {
    error: null,
    loading: true,
    submitted: false,
  });
};

const submitProfileFailed = (state, action) => {
  return updateObject(state, {
    error: action.error,
    loading: false,
  });
};

const submitProfileSuccess = (state) => {
  return updateObject(state, {
    editingProfile: {
      UserId: null,
      FirstName: '',
    },
    error: null,
    loading: false,
    submitted: true,
    showProfileForm: false,
  });
};

const initProfileForm = (state, action) => {
  return updateObject(state, {
    editingProfile: {
      UserId: action.UserId,
      FirstName: action.FirstName,
      Color: action.Color,
      LastName: action.LastName,
      DateBirthDay: action.DateBirthDay,
      NickName: action.NickName,
      ThumbURL: action.ThumbURL,
      ProfileStatus: action.ProfileStatus,
    },
    showProfileForm: action.ShowModal !== false,
  });
};

const closeProfileForm = (state) => {
  return updateObject(state, {
    editingProfile: {
      UserId: null,
      FirstName: '',
    },
    showProfileForm: false,
  });
};

const setManagedUsers = (state, action) => {
  return updateObject(state, {
    managedUsers: action.managedUsers,
  });
};

const updateEmailStart = (state) => {
  return updateObject(state, {
    error: null,
    loading: true,
    emailUpdated: false,
  });
};

const updateEmailSuccess = (state, action) => {
  return updateObject(state, {
    loading: false,
    emailUpdated: true,
    signedInAs: {
      ...state.signedInAs,
      Email: action.Email,
    },
  });
};

const updateEmailFailed = (state, action) => {
  return updateObject(state, {
    loading: false,
    error: action.error,
  });
};

const updatePasswordStart = (state) => {
  return updateObject(state, {
    error: null,
    loading: true,
    passwordUpdated: false,
  });
};

const updatePasswordSuccess = (state) => {
  return updateObject(state, {
    loading: false,
    passwordUpdated: true,
  });
};

const updatePasswordFailed = (state, action) => {
  return updateObject(state, {
    loading: false,
    error: action.error,
  });
};

const updatePostingAs = (state, action) => {
  const managedUserIndex = state.managedUsers.findIndex((managedUser) => managedUser.UserId === action.UserId);
  return updateObject(state, {
    postingAs: managedUserIndex !== -1 ? state.managedUsers[managedUserIndex] : state.signedInAs,
    postingAsUserUpdated: true,
  });
};

const revertToSignedInAs = (state) => {
  localStorage.setItem('postingAs', JSON.stringify({ UserId: state.signedInAs.UserId }));
  if (state.signedInAs.FirstName.length === 0) {
    return state;
  }
  return updateObject(state, {
    postingAs: { ...state.signedInAs },
    postingAsUserUpdated: false,
  });
};

const createManagedUserStart = (state) => {
  return updateObject(state, {
    loading: true,
    error: null,
    submitted: false,
  });
};

const createManagedUserFailed = (state, action) => {
  return updateObject(state, {
    loading: false,
    error: action.error,
  });
};

const createManagedUserSuccess = (state) => {
  // const newManagedUser = { UserId: action.UserId, FirstName: '' };
  // return updateObject(state, {
  //   loading: false,
  //   error: null,
  //   editingProfile: {
  //     UserId: action.UserId,
  //     FirstName: '',
  //   },
  //   showProfileForm: true,
  // });
  return updateObject(state, {
    editingProfile: {
      UserId: null,
      FirstName: '',
    },
    error: null,
    loading: false,
    submitted: true,
    showProfileForm: false,
  });
};

const deleteManagedUserStart = (state) => {
  return updateObject(state, {
    loading: true,
    error: null,
    submitted: false,
  });
};

const deleteManagedUserFailed = (state, action) => {
  return updateObject(state, {
    loading: false,
    error: action.error,
  });
};

const deleteManagedUserSuccess = (state) => {
  return updateObject(state, {
    loading: false,
    error: null,
    submitted: true,
    showProfileForm: false,
  });
};

const userSearchStart = (state) => {
  return updateObject(state, {
    loading: true,
    error: null,
  });
};

const userSearchSuccess = (state, action) => {
  return updateObject(state, {
    loading: false,
    error: null,
    userSearchResults: action.users,
  });
};

const userSearchFailed = (state, action) => {
  return updateObject(state, {
    loading: false,
    error: action.error,
  });
};

const logoutSuccess = (state) => {
  return updateObject(state, {
    ...initialState,
  });
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.FETCH_PROFILE_SUCCESS:
      return fetchProfileSuccess(state, action);
    case actionTypes.SET_PROFILE_IMAGE:
      return setProfileImage(state, action);
    case actionTypes.UPLOAD_PROFILE_IMAGE_FAILED:
      return uploadProfileImageFailed(state, action);
    case actionTypes.UPLOAD_PROFILE_IMAGE_START:
      return uploadProfileImageStart(state, action);
    case actionTypes.FETCH_PROFILE_FAILED:
      return fetchProfileFailed(state, action);
    case actionTypes.SUBMIT_PROFILE_START:
      return submitProfileStart(state, action);
    case actionTypes.SUBMIT_PROFILE_FAILED:
      return submitProfileFailed(state, action);
    case actionTypes.SUBMIT_PROFILE_SUCCESS:
      return submitProfileSuccess(state, action);
    case actionTypes.FETCH_PROFILE_START:
      return fetchProfileStart(state, action);
    case actionTypes.FETCH_PROFILE_RETURNS_NEW_USER:
      return fetchProfileReturnsNewUser(state, action);
    case actionTypes.INIT_PROFILE_FORM:
      return initProfileForm(state, action);
    case actionTypes.CLOSE_PROFILE_FORM:
      return closeProfileForm(state, action);
    case actionTypes.UPDATE_EMAIL_START:
      return updateEmailStart(state, action);
    case actionTypes.UPDATE_EMAIL_SUCCESS:
      return updateEmailSuccess(state, action);
    case actionTypes.UPDATE_EMAIL_FAILED:
      return updateEmailFailed(state, action);
    case actionTypes.UPDATE_PASSWORD_START:
      return updatePasswordStart(state, action);
    case actionTypes.UPDATE_PASSWORD_SUCCESS:
      return updatePasswordSuccess(state, action);
    case actionTypes.UPDATE_PASSWORD_FAILED:
      return updatePasswordFailed(state, action);
    case actionTypes.SET_MANAGED_USERS:
      return setManagedUsers(state, action);
    case actionTypes.UPDATE_POSTING_AS:
      return updatePostingAs(state, action);
    case actionTypes.REVERT_TO_SIGNED_IN_AS:
      return revertToSignedInAs(state, action);
    case actionTypes.CREATE_MANAGED_ACCOUNT_START:
      return createManagedUserStart(state, action);
    case actionTypes.CREATE_MANAGED_ACCOUNT_FAILED:
      return createManagedUserFailed(state, action);
    case actionTypes.CREATE_MANAGED_ACCOUNT_SUCCESS:
      return createManagedUserSuccess(state, action);
    case actionTypes.DELETE_MANAGED_ACCOUNT_SUCCESS:
      return deleteManagedUserSuccess(state, action);
    case actionTypes.DELETE_MANAGED_ACCOUNT_FAILED:
      return deleteManagedUserFailed(state, action);
    case actionTypes.DELETE_MANAGED_ACCOUNT_START:
      return deleteManagedUserStart(state, action);
    case actionTypes.USER_SEARCH_START:
      return userSearchStart(state, action);
    case actionTypes.USER_SEARCH_SUCCESS:
      return userSearchSuccess(state, action);
    case actionTypes.USER_SEARCH_FAILED:
      return userSearchFailed(state, action);
    case actionTypes.LOGOUT_SUCCESS:
      return logoutSuccess(state, action);
    case actionTypes.USER_EDIT_EMOJI_TONE:
      return editEmojiTone(state, action);
    default:
      return state;
  }
};

export default reducer;
